import { jsx as _jsx } from "react/jsx-runtime";
import { useCombobox } from 'downshift';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { useVirtual } from 'react-virtual';
import { isNumber } from '../../../common.types.js';
import { itemToString } from '../BKJComboBoxAsync.utils.js';
import { useBKJComboBoxAsyncContextHooks } from './BKJComboBoxAsync.context.hooks.js';
import i18next from 'i18next';
const loadElement = (name) => ({
    type: 'Loader',
    id: 'Loader',
    value: 'Loader',
    key: 'Loader',
    name,
    disabled: true,
    label: 'Loader',
});
const noResultsElement = (name) => ({
    type: 'NoResults',
    id: 'No Results',
    value: i18next.t('translations:NO_RESULTS'),
    key: 'No Results',
    name,
    disabled: true,
    label: i18next.t('translations:NO_RESULTS'),
});
export const BKJComboBoxAsyncContext = createContext({});
export const BKJComboBoxAsyncProvider = ({ icon, type, name, value, label, error, action, variant, options: __options__ = [], context, disabled, onChange, children, required, searchKey, placeholder, searchValue, onSearchChange, portalTargetElement, }) => {
    const { pageInfo, isLoading } = useContext(context);
    const hasNextPage = pageInfo?.hasNextPage;
    const [{ anchorElement, dropdownElement }, { setAnchorElement, setDropdownElement }] = useBKJComboBoxAsyncContextHooks();
    const isDropdownReverse = dropdownElement?.style?.top === 'auto' && dropdownElement?.style?.bottom === '0px';
    const options = useMemo(() => {
        let __o__ = [...__options__];
        if (hasNextPage || isLoading)
            __o__ = __o__.concat(loadElement(name));
        if (__o__.length === 0)
            __o__ = [noResultsElement(name)].concat(__o__);
        return __o__.map((opts, index) => {
            return { ...opts, index };
        });
    }, [__options__, hasNextPage, isLoading, name]);
    const parentRef = useRef(null);
    const rowVirtualizer = useVirtual({
        size: options.length,
        parentRef,
        keyExtractor: (index) => options[index].id,
    });
    const { reset, isOpen, openMenu, closeMenu, toggleMenu, selectedItem, getMenuProps, getItemProps, getInputProps, getLabelProps, getComboboxProps, highlightedIndex, setHighlightedIndex, getToggleButtonProps, setInputValue: __downShiftSetInputValue__, } = useCombobox({
        inputValue: searchValue,
        itemToString,
        items: options,
        initialSelectedItem: value,
        scrollIntoView: () => void 0,
        onHighlightedIndexChange: ({ highlightedIndex: __i__ = 0, type }) => {
            const highlightedItem = options[__i__];
            let INDEX = __i__;
            if (highlightedItem) {
                if (['Group', 'Loader', 'NoResults'].includes(highlightedItem.type)) {
                    if (type === '__input_keydown_arrow_down__')
                        INDEX = __i__ + 1;
                    if (type === '__input_keydown_arrow_up__')
                        INDEX = __i__ - 1;
                    if (INDEX > options.length - 1)
                        INDEX = 0;
                }
            }
            if (isNumber(INDEX) && INDEX >= 0) {
                rowVirtualizer.scrollToIndex(INDEX);
            }
            setHighlightedIndex(INDEX);
        },
        onSelectedItemChange: (event) => {
            const { selectedItem } = event;
            const nextValue = {
                ...selectedItem,
                name,
                value: selectedItem,
            };
            if (searchValue !== '') {
                __downShiftSetInputValue__('');
                onSearchChange({
                    target: {
                        value: '',
                        name: searchKey,
                    },
                });
            }
            if (onChange)
                onChange({
                    target: nextValue,
                });
            closeMenu();
            anchorElement?.blur();
            return void 0;
        },
        onIsOpenChange: ({ isOpen: __isOpen__, highlightedIndex: __highlightedIndex__, selectedItem: __selectedItem__, }) => {
            if (__isOpen__) {
                if (__selectedItem__) {
                    if (options.some((option) => {
                        return option.index === __selectedItem__.index;
                    }))
                        rowVirtualizer.scrollToIndex(__selectedItem__.index, { align: 'start' });
                }
            }
            if (!__isOpen__ && searchValue !== '') {
                __downShiftSetInputValue__('');
                onSearchChange({
                    target: {
                        value: '',
                        name: searchKey,
                    },
                });
            }
        },
    });
    const handleInputFocus = useCallback(openMenu, [openMenu]);
    const handleInputKeyDown = useCallback(({ key, nativeEvent }) => {
        switch (action) {
            case 'OpenToggle':
                switch (key) {
                    case 'Escape':
                    case 'Enter':
                        nativeEvent.preventDownshiftDefault = true;
                        if (isOpen && searchValue !== '')
                            onSearchChange({ target: { value: '', name: searchKey } });
                        if (isOpen && searchValue === '') {
                            closeMenu();
                            anchorElement?.blur();
                        }
                        break;
                }
                break;
            case 'ClearInputValue': {
                break;
            }
        }
    }, [action, anchorElement, closeMenu, isOpen, onSearchChange, searchKey, searchValue]);
    useEffect(() => {
        if (options.length === 0)
            setHighlightedIndex(-1);
        else if (searchValue !== '' && highlightedIndex === -1 && options.length > 0)
            setHighlightedIndex(0);
    }, [highlightedIndex, options, searchValue, setHighlightedIndex]);
    return (_jsx(BKJComboBoxAsyncContext.Provider, { value: {
            type,
            name,
            label,
            reset,
            value,
            error,
            action,
            isOpen,
            options,
            context,
            variant,
            onChange,
            required,
            disabled,
            openMenu,
            parentRef,
            searchKey,
            closeMenu,
            toggleMenu,
            searchValue,
            placeholder,
            selectedItem,
            getItemProps,
            getMenuProps,
            getInputProps,
            getLabelProps,
            anchorElement,
            rowVirtualizer,
            onSearchChange,
            dropdownElement,
            setAnchorElement,
            highlightedIndex,
            getComboboxProps,
            isDropdownReverse,
            setDropdownElement,
            portalTargetElement,
            setHighlightedIndex,
            getToggleButtonProps,
            onInputFocus: handleInputFocus,
            onInputKeyDown: handleInputKeyDown,
            leftIconProps: icon ? { iconName: icon } : undefined,
        }, children: children }));
};
export const useBKJComboBoxAsyncContext = () => useContext(BKJComboBoxAsyncContext);
//# sourceMappingURL=BKJComboBoxAsync.context.js.map